import {
  infiniteQueryOptions,
  keepPreviousData,
  useInfiniteQuery,
  useSuspenseInfiniteQuery,
} from "@tanstack/react-query";

import { ordersApi } from "@shared/api";
import { useScrollFetchRef } from "@shared/libs/api-client/hooks";

import { constants, defaultPageSize } from "./constants";

type UseTradeOrdersParams = {
  assets: string[];
  pageSize?: number;
  statuses: string[];
  types: string[];
};

/* eslint-disable perfectionist/sort-objects */
export const tradeOrdersQueryOptions = ({
  pageSize = defaultPageSize,
  assets = [],
  statuses = [],
  types = [],
}: UseTradeOrdersParams) =>
  infiniteQueryOptions({
    queryKey: constants.list({ pageSize, assets, statuses, types }),
    queryFn: ({ pageParam }) =>
      ordersApi.getApiV1TradeOrders({
        page: pageParam,
        pageSize,
        assets: assets.length > 0 ? assets.join(",") : undefined,
        statuses: statuses.length > 0 ? statuses.join(",") : undefined,
        types: types.length > 0 ? types.join(",") : undefined,
      }),
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    refetchOnMount: "always",
    getNextPageParam: (lastPage) => {
      const { pagination } = lastPage.data;
      if (pagination.page >= pagination.pageCount - 1) {
        return undefined;
      }
      return pagination.page + 1;
    },
    select: (data) => {
      const flatList = data.pages.flatMap((page) => page.data.tradeOrders);

      const lastPage = data.pages[data.pages.length - 1];

      return {
        list: flatList,
        pagination: lastPage.data.pagination,
        sort: lastPage.data.sort,
      };
    },
  });

export const useTradeOrders = (params: UseTradeOrdersParams) => {
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    refetch,
    isFetchingNextPage,
    isPending,
  } = useSuspenseInfiniteQuery(tradeOrdersQueryOptions(params));

  const loadMoreRef = useScrollFetchRef({
    fetch: fetchNextPage,
    isFetching: isFetchingNextPage,
    hasNext: hasNextPage,
  });

  return {
    error,
    loadMoreRef,
    pagination: data.pagination,
    refetch,
    tradeOrders: data.list,
    sort: data.sort,
    isPending: isPending || isFetchingNextPage,
  };
};

export const useTradeOrdersQuery = (params: UseTradeOrdersParams) => {
  const { data, error, isError, isLoading, refetch } = useInfiniteQuery(
    tradeOrdersQueryOptions(params),
  );

  return {
    error,
    isError,
    isLoading,
    refetch,
    tradeOrders: data?.list ?? [],
  };
};
